'use client';

import { AppButton } from '@/components/design/forms';
import { CheckCircle2, Users, QrCode, Trophy, CreditCard, Smartphone } from 'lucide-react';
import { festivalLandingStaticPrices } from '@/config/festivalLandingStaticPrices';
import { FormattedPrice } from '@/components/money/FormattedPrice';
import { Modal } from '@/components/design/layout/Modal';
import { useTranslations } from 'next-intl';

interface RegistrationInfoModalProps {
  onClose: () => void;
}

export function RegistrationInfoModal({ onClose }: RegistrationInfoModalProps) {
  const t = useTranslations('strings.registrationInfoModal');

  return (
    <Modal
      open
      onClose={onClose}
      overlayPreset="registrationInfo"
      panelClassName="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
    >
        {/* Header */}
        <div className="bg-gradient-to-r from-fest-gold to-fest-gold-mid p-8 text-white">
          <h2 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('title')}
          </h2>
          <p className="text-white/90">{t('subtitle')}</p>
        </div>

        {/* Content */}
        <div className="p-8 space-y-8">
          {/* Step 1 */}
          <div className="flex gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-gradient-to-br from-fest-gold to-fest-gold-mid rounded-2xl flex items-center justify-center text-white text-2xl font-bold">
                1
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <Users className="w-6 h-6 text-fest-gold" />
                <h3 className="text-xl font-bold text-fest-heading">{t('step1Title')}</h3>
              </div>
              <p className="text-fest-subtle-text mb-3">{t('step1Body')}</p>
              <div className="bg-fest-canvas rounded-xl p-4">
                <div className="flex items-start gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-fest-apple-green flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-fest-heading">
                    <strong>{t('autoSaveTitle')}</strong> {t('autoSaveBody')}
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-fest-apple-green flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-fest-heading">
                    <strong>{t('securityTitle')}</strong> {t('securityBody')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-gradient-to-br from-fest-gold to-fest-gold-mid rounded-2xl flex items-center justify-center text-white text-2xl font-bold">
                2
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <QrCode className="w-6 h-6 text-fest-gold" />
                <h3 className="text-xl font-bold text-fest-heading">{t('step2Title')}</h3>
              </div>
              <p className="text-fest-subtle-text mb-3">{t('step2Body')}</p>
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-3 bg-fest-canvas rounded-lg">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                    <QrCode className="w-5 h-5 text-fest-gold" />
                  </div>
                  <span className="text-sm text-fest-heading font-medium">{t('qrLabel')}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-fest-canvas rounded-lg">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-fest-gold" />
                  </div>
                  <span className="text-sm text-fest-heading font-medium">{t('cardLabel')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-gradient-to-br from-fest-gold to-fest-gold-mid rounded-2xl flex items-center justify-center text-white text-2xl font-bold">
                3
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <Trophy className="w-6 h-6 text-fest-gold" />
                <h3 className="text-xl font-bold text-fest-heading">{t('step3Title')}</h3>
              </div>
              <p className="text-fest-subtle-text mb-3">{t('step3Body')}</p>
              <div className="bg-fest-canvas rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-fest-heading">{t('disciplineMetropolitan')}</span>
                  <span className="text-sm font-bold text-fest-gold whitespace-nowrap">
                    <FormattedPrice amount={festivalLandingStaticPrices.registrationChampionshipMetropolitangoNational} className="inline" />
                    {'–'}
                    <FormattedPrice amount={festivalLandingStaticPrices.registrationChampionshipMetropolitangoInternational} className="inline" />{' '}
                    {t('perCategory')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-fest-heading">{t('disciplineBakuTrophy')}</span>
                  <span className="text-sm font-bold text-fest-gold">
                    <FormattedPrice amount={festivalLandingStaticPrices.registrationChampionshipBakuTrophy} className="inline" /> {t('perCategory')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="flex gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-gradient-to-br from-fest-gold to-fest-gold-mid rounded-2xl flex items-center justify-center text-white text-2xl font-bold">
                4
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <Smartphone className="w-6 h-6 text-fest-gold" />
                <h3 className="text-xl font-bold text-fest-heading">{t('step4Title')}</h3>
              </div>
              <p className="text-fest-subtle-text mb-3">{t('step4Intro')}</p>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-fest-apple-green flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-fest-heading">{t('cab1')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-fest-apple-green flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-fest-heading">{t('cab2')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-fest-apple-green flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-fest-heading">{t('cab3')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-fest-apple-green flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-fest-heading">{t('cab4')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t-2 border-fest-border-warm p-6 bg-fest-canvas">
          <AppButton
            onClick={onClose}
            className="w-full px-8 py-4 bg-gradient-to-r from-fest-gold to-fest-gold-mid text-white rounded-xl font-bold hover:shadow-lg transition-all"
          >
            {t('cta')}
          </AppButton>
        </div>
    </Modal>
  );
}
